import { useParams } from "react-router";
import { useMutation, useQueryClient } from "@tanstack/react-query";

import Typography from "core/components/Typography";
import { ROLES } from "core/constants";
import useActiveMembership from "core/hooks/useActiveMembership";
import useAuthenticatedMutation from "core/hooks/useAuthenticatedMutation";
import { useOrganizationByIdQuery } from "core/queries";
import DetailsViewer from "modules/admin/DetailsViewer";
import OrgIntegrationsForm from "modules/admin/OrgIntegrationsForm";
import { updateOrganization } from "modules/admin/actions";
import { toast } from "sonner";

const OrganizationIntegrationsPage = () => {
  const { orgId: organizationId } = useParams();
  const queryClient = useQueryClient();
  const { activeMembership } = useActiveMembership();

  const { data: organization, isLoading } = useOrganizationByIdQuery({
    organizationId,
  });

  const updateOrg = useAuthenticatedMutation((req) =>
    updateOrganization({ ...req, organizationId })
  );

  const orgMutation = useMutation({
    mutationFn: updateOrg,
    onSuccess: (_, updatedOrg) => {
      queryClient.setQueryData(
        ["organizations", { organizationId }],
        (cachedOrg) => {
          if (!cachedOrg) return cachedOrg;
          return {
            ...cachedOrg,
            ...updatedOrg,
            isUpdated: true,
            resetUpdateParams: {
              queryKey: ["organizations", { organizationId }],
              rowId: organizationId,
            },
          };
        }
      );
      queryClient.invalidateQueries({ queryKey: ["organizations"] });
      toast.success("Integrations have been updated.");
    },
  });

  const canEdit = activeMembership.role === ROLES.systemAdmin;

  return (
    <div style={{ maxWidth: "500px" }}>
      <Typography variant="h2" noMargin style={{ marginBottom: "5px" }}>
        Integrations
      </Typography>

      {canEdit ? (
        <OrgIntegrationsForm
          onSubmit={orgMutation.mutate}
          initialValues={{ callbackUrls: organization?.callbackUrls }}
          isFetching={isLoading}
          isSubmitting={orgMutation.isPending}
          error={orgMutation.error}
          submitLabel="Save Changes"
        />
      ) : (
        <DetailsViewer
          isLoading={isLoading}
          data={[
            {
              label: "Callback URLs",
              value: organization?.callbackUrls,
            },
          ]}
        />
      )}
    </div>
  );
};

export default OrganizationIntegrationsPage;
